/**
 * DuplicateReviewDialog — fires at SAVE time when the backend's
 * `/quotations/check-duplicates` returns one or more recent quotations
 * that look like they're for the same customer + overlapping items.
 *
 * Why this exists
 * ---------------
 * Staff occasionally save a slightly-edited quotation as a brand-new
 * row (e.g. they tweaked a discount, then forgot to use the Edit
 * button, or `?edit=ID` was lost on a reload). Two rows then count
 * as two interactions in analytics, inflating volume + staff stats.
 *
 * We intercept BEFORE save. Three actions:
 *   • Replace old with new   → soft-deletes the matched row via
 *                              POST /:old_id/supersede after the new
 *                              quotation is created.
 *   • Keep both (different)  → calls POST /:new_id/mark-distinct so
 *                              the silent dedupe never collapses them.
 *   • Cancel                 → modal closes, nothing happens, staff
 *                              can adjust the new quotation first.
 *
 * Props
 * -----
 *   open        : bool          — dialog visibility
 *   onClose     : ()  → void    — close without acting (Cancel)
 *   matches     : object[]      — from /check-duplicates: { id,
 *                                 quotation_number, created_at,
 *                                 created_by_name, grand_total,
 *                                 customer_name, items_count,
 *                                 items_preview[], overlap_score }
 *   pendingPayload : object     — info-only summary of what the
 *                                 staff is about to save (for the
 *                                 right-hand "NEW" column).
 *   onReplace   : (oldId) → Promise  — picked one match + Replace
 *   onKeepBoth  : () → Promise       — confirm two distinct quotes
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { AlertTriangle, ArrowLeftRight, GitMerge, X, FileText } from 'lucide-react';
import { formatINR } from '../../lib/store';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function ItemPreviewList({ items, max = 8 }) {
  if (!items || items.length === 0) {
    return <p className="text-xs text-[#8F9485] italic">No items in preview</p>;
  }
  const shown = items.slice(0, max);
  const extra = items.length - max;
  return (
    <ul className="space-y-1 text-xs">
      {shown.map((it, idx) => (
        // Composite key from item fields so React preserves row identity
        // across re-renders even when the same description appears twice.
        <li key={`${it.product_code || ''}-${it.description || ''}-${idx}`} className="flex justify-between gap-2 leading-tight">
          <span className="truncate text-[#1f2a16]">
            {it.description || it.product_code || '—'}
            {it.quantity ? <span className="text-[#8F9485]"> × {it.quantity}{it.unit ? ` ${it.unit}` : ''}</span> : null}
          </span>
          {it.amount ? (
            <span className="shrink-0 text-[#1f2a16] font-medium">{formatINR(it.amount)}</span>
          ) : null}
        </li>
      ))}
      {extra > 0 && (
        <li className="text-[#8F9485] italic">+ {extra} more…</li>
      )}
    </ul>
  );
}

export function DuplicateReviewDialog({
  open,
  onClose,
  matches = [],
  pendingPayload = null,
  onReplace,
  onKeepBoth,
}) {
  const [busy, setBusy] = useState(false);
  // Track which row the staff picked. Defaults (in render) to the first
  // match so the "Replace" button is immediately actionable. When the
  // dialog opens with a fresh set of matches that don't include the
  // last `picked` id, the fallback to `matches[0]` still gives a sane
  // default — no useEffect needed.
  const [picked, setPicked] = useState(null);
  const matchIds = matches.map(m => m.id);
  const selectedId = (picked && matchIds.includes(picked)) ? picked : (matches[0]?.id || '');
  const setSelectedId = setPicked;

  const newCustomerName = pendingPayload?.customer?.name || '';
  const newItems = (pendingPayload?.items || []).map(it => ({
    description: it.description || it.product_name || '',
    product_code: it.product_code || '',
    quantity: it.quantity || 0,
    unit: it.unit || '',
    rate: it.rate || 0,
    amount: it.amount || 0,
  }));
  const newTotal = newItems.reduce((s, it) => s + (it.amount || 0), 0);

  const handleReplace = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await onReplace(selectedId);
    } finally {
      setBusy(false);
    }
  };

  const handleKeepBoth = async () => {
    setBusy(true);
    try {
      await onKeepBoth();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent
        className="max-w-4xl bg-[#F5F2E8] border-[#1f2a16]/20"
        data-testid="duplicate-review-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1f2a16]">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Possible duplicate quotation
          </DialogTitle>
          <DialogDescription className="text-[#5b6450]">
            We found {matches.length} recent quotation{matches.length === 1 ? '' : 's'} for
            <strong> {newCustomerName || 'this customer'}</strong> with overlapping items.
            Pick one to <strong>replace</strong>, or confirm both are different orders.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
          {/* LEFT: existing matches (radio-selectable) */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#5b6450]">
              Existing quotation{matches.length === 1 ? '' : 's'}
            </p>
            {matches.map((m) => {
              const isPicked = selectedId === m.id;
              return (
                <Card
                  key={m.id}
                  data-testid={`duplicate-match-${m.id}`}
                  className={`p-3 cursor-pointer transition border ${
                    isPicked
                      ? 'border-amber-600 bg-amber-50/60 ring-2 ring-amber-500/40'
                      : 'border-[#1f2a16]/15 hover:border-[#1f2a16]/40 bg-white'
                  }`}
                  onClick={() => setSelectedId(m.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <input
                          type="radio"
                          checked={isPicked}
                          onChange={() => setSelectedId(m.id)}
                          className="cursor-pointer"
                          data-testid={`duplicate-match-radio-${m.id}`}
                        />
                        <FileText className="w-3.5 h-3.5 text-[#5b6450]" />
                        <span className="font-semibold text-sm text-[#1f2a16] truncate">
                          {m.quotation_number || m.id.slice(0, 8)}
                        </span>
                        <Badge variant="outline" className="text-[10px] py-0 px-1 border-amber-600/50 text-amber-700">
                          {Math.round((m.overlap_score || 0) * 100)}% match
                        </Badge>
                      </div>
                      <p className="text-[11px] text-[#5b6450]">
                        {fmtDate(m.created_at)} · by {m.created_by_name || 'Unknown'} · {m.items_count} item{m.items_count === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-[#1f2a16]">{formatINR(m.grand_total || 0)}</p>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-dashed border-[#1f2a16]/15">
                    <ItemPreviewList items={m.items_preview || []} />
                  </div>
                </Card>
              );
            })}
          </div>

          {/* RIGHT: about-to-save quotation */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              About to save (new)
            </p>
            <Card className="p-3 border border-emerald-600/40 bg-emerald-50/40">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <FileText className="w-3.5 h-3.5 text-emerald-700" />
                    <span className="font-semibold text-sm text-[#1f2a16]">
                      {newCustomerName || 'New quotation'}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#5b6450]">
                    {newItems.length} item{newItems.length === 1 ? '' : 's'}
                  </p>
                </div>
                <p className="text-sm font-semibold text-[#1f2a16] shrink-0">
                  {formatINR(newTotal)}
                </p>
              </div>
              <div className="mt-2 pt-2 border-t border-dashed border-emerald-700/20">
                <ItemPreviewList items={newItems} />
              </div>
            </Card>
          </div>
        </div>

        {/* Action footer */}
        <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-[#1f2a16]/15">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="sm:order-1 order-3"
            data-testid="duplicate-cancel-btn"
          >
            <X className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleKeepBoth}
            disabled={busy}
            className="sm:order-2 order-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50"
            data-testid="duplicate-keepboth-btn"
          >
            <ArrowLeftRight className="w-4 h-4 mr-1.5" />
            Keep both (different)
          </Button>
          <Button
            onClick={handleReplace}
            disabled={busy || !selectedId}
            className="sm:order-3 order-1 bg-amber-600 hover:bg-amber-700 text-white sm:ml-auto"
            data-testid="duplicate-replace-btn"
          >
            <GitMerge className="w-4 h-4 mr-1.5" />
            Replace selected with new
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
